/**
 * Shopify Carousel Component
 * Handles infinite scrolling, responsive behavior, and accessibility
 */

class ShopifyCarousel {
  constructor(element) {
    this.carousel = element;
    this.track = element.querySelector('.carousel__track');
    this.items = Array.from(element.querySelectorAll('.carousel__item'));
    
    // Configuration from data attributes
    this.config = {
      animationLength: parseInt(element.dataset.animationLength) || 5,
      itemSize: element.dataset.itemSize || 'medium',
      itemsPerView: parseInt(element.dataset.itemsPerView) || 5,
      pauseOnHover: element.dataset.pauseOnHover === 'true',
      hoverExpand: element.dataset.hoverExpand === 'true'
    };
    
    // State
    this.isAnimating = true;
    this.currentTransform = 0;
    this.itemWidth = 0;
    this.totalWidth = 0;
    this.animationId = null;
    this.resizeTimeout = null;
    
    // Drag state
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.dragOffset = 0;
    this.hasDragged = false;
    
    // Native-scroll (touch) state
    this.driftRAF = null;
    this.resumeTimeout = null;
    this.driftPaused = false;
    this.driftAccum = 0;
    this.setWidth = 0;
    
    this.init();
  }
  
  init() {
    if (this.items.length === 0) return;
    
    // On touch devices, present the strip as a natively swipeable row that ALSO drifts on
    // its own (see setupNativeScroll). We skip the clone/transform/custom-drag marquee used
    // on desktop because that combination fought itself on touch and snapped back on every
    // swipe. Matching CSS is in the @media (pointer: coarse) block. Desktop keeps the
    // transform-based auto-scroll marquee below.
    if (window.matchMedia('(pointer: coarse)').matches) {
      this.setupNativeScroll();
      this.setupAccessibility();
      return;
    }
    
    this.setupInfiniteScroll();
    this.calculateDimensions();
    this.setupAnimation();
    this.setupEventListeners();
    this.setupAccessibility();
    
    // Start animation
    this.startAnimation();
  }
  
  setupNativeScroll() {
    // Touch path: a natively swipeable, seamlessly looping strip that also auto-drifts.
    // Approach: clone the set twice (3 identical copies), park the scroll position in the
    // middle copy, and nudge container.scrollLeft a little each frame for the drift. The
    // browser handles the swipe + momentum natively. Touching pauses the drift; it resumes
    // ~1.5s after release. A scroll handler wraps the position by whole set-widths, which is
    // invisible because the copies are identical -> infinite in both directions.
    this.isAnimating = false;
    this.driftPaused = false;
    this.driftAccum = 0;
    this.setWidth = 0;
    this.container = this.carousel.querySelector('.carousel__container');
    if (!this.container) return;
    
    this.track.style.animation = 'none';
    
    // Clone two extra full sets (original + 2 clones = 3 copies) for seamless wrapping.
    const clonesNeeded = this.items.length * 2;
    for (let i = 0; i < clonesNeeded; i++) {
      const clone = this.items[i % this.items.length].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      this.track.appendChild(clone);
    }
    
    // Measure and wire up after layout so widths are real.
    requestAnimationFrame(() => {
      this.setWidth = this.measureSetWidth();
      if (this.setWidth > 0) this.container.scrollLeft = this.setWidth;
      
      // Keep the scroll position parked in the middle copy so it can wrap either direction.
      const normalize = () => {
        if (this._normalizing || this.setWidth <= 0) return;
        const sl = this.container.scrollLeft;
        if (sl >= this.setWidth * 2) {
          this._normalizing = true;
          this.container.scrollLeft = sl - this.setWidth;
          this._normalizing = false;
        } else if (sl < this.setWidth) {
          this._normalizing = true;
          this.container.scrollLeft = sl + this.setWidth;
          this._normalizing = false;
        }
      };
      this.container.addEventListener('scroll', normalize, { passive: true });
      
      // Pause the drift while the visitor is interacting; resume shortly after they let go.
      const pause = () => {
        this.driftPaused = true;
        if (this.resumeTimeout) clearTimeout(this.resumeTimeout);
      };
      const scheduleResume = () => {
        if (this.resumeTimeout) clearTimeout(this.resumeTimeout);
        this.resumeTimeout = setTimeout(() => { this.driftPaused = false; }, 1500);
      };
      this.container.addEventListener('touchstart', pause, { passive: true });
      this.container.addEventListener('touchend', scheduleResume, { passive: true });
      this.container.addEventListener('pointerdown', pause);
      this.container.addEventListener('pointerup', scheduleResume);
      
      // Auto-drift (frame-rate independent), unless the visitor prefers reduced motion.
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && this.setWidth > 0) {
        const pxPerMs = this.setWidth / (this.config.animationLength * 10 * 1000);
        let last = performance.now();
        const step = (now) => {
          const dt = now - last;
          last = now;
          if (!this.driftPaused) {
            this.driftAccum += pxPerMs * dt;
            const whole = Math.floor(this.driftAccum);
            if (whole >= 1) {
              this.driftAccum -= whole;
              this.container.scrollLeft += whole;
            }
          }
          this.driftRAF = requestAnimationFrame(step);
        };
        this.driftRAF = requestAnimationFrame(step);
      }
    });
  }
  
  setupInfiniteScroll() {
    // Clone items for seamless looping - need more clones for drag interactions
    const itemsToClone = Math.max(this.config.itemsPerView * 4, this.items.length * 3);
    
    // Clone items and append to track
    for (let i = 0; i < itemsToClone; i++) {
      const originalItem = this.items[i % this.items.length];
      const clonedItem = originalItem.cloneNode(true);
      clonedItem.setAttribute('aria-hidden', 'true'); // Hide clones from screen readers
      this.track.appendChild(clonedItem);
    }
  }
  
  // Measure the true rendered width of one full set of original items, straight
  // from the laid-out DOM. This is the horizontal distance between item 0 and its
  // first clone (the item at index items.length), which is exactly what the loop
  // must travel for a seamless wrap. Measuring avoids relying on a guessed
  // itemWidth that never matches the real rendered size (aspect-ratio posters,
  // responsive breakpoints, hover scaling), which is what caused the jump.
  measureSetWidth() {
    const children = this.track.children;
    if (children.length > this.items.length) {
      const distance = children[this.items.length].offsetLeft - children[0].offsetLeft;
      if (distance > 0) return distance;
    }
    // Fallback to the computed estimate if measurement isn't available yet
    return (this.itemWidth + 16) * this.items.length;
  }
  
  calculateDimensions() {
    const containerWidth = this.carousel.offsetWidth;
    const gap = 16; // 1rem in pixels
    
    // Calculate responsive items per view
    let itemsPerView = this.config.itemsPerView;
    
    if (window.innerWidth <= 767) {
      itemsPerView = Math.min(3, this.items.length);
    } else if (window.innerWidth <= 1199) {
      itemsPerView = Math.min(Math.floor(itemsPerView * 0.8), this.items.length);
    }
    
    // Calculate item width based on container and gaps
    this.itemWidth = (containerWidth - (gap * (itemsPerView - 1))) / itemsPerView;
    
    // Calculate total width for animation
    const allItems = this.track.children;
    this.totalWidth = (this.itemWidth + gap) * allItems.length;
    
    // Set CSS custom property for animation
    const animationDuration = this.config.animationLength * 10; // Convert to seconds
    this.track.style.animationDuration = `${animationDuration}s`;
  }
  
  setupAnimation() {
    // Calculate animation distance from the real rendered layout
    const singleSetWidth = this.measureSetWidth();
    
    // Update CSS animation
    const keyframes = `
      @keyframes carousel-scroll-${this.carousel.dataset.sectionId} {
        from { transform: translateX(0); }
        to { transform: translateX(-${singleSetWidth}px); }
      }
    `;
    
    // Inject keyframes
    let styleSheet = document.getElementById(`carousel-keyframes-${this.carousel.dataset.sectionId}`);
    if (!styleSheet) {
      styleSheet = document.createElement('style');
      styleSheet.id = `carousel-keyframes-${this.carousel.dataset.sectionId}`;
      document.head.appendChild(styleSheet);
    }
    styleSheet.textContent = keyframes;
    
    // Apply animation
    this.track.style.animation = `carousel-scroll-${this.carousel.dataset.sectionId} ${this.config.animationLength * 10}s linear infinite`;
  }
  
  setupEventListeners() {
    // Resize handling with debounce
    window.addEventListener('resize', this.debounce(() => {
      this.calculateDimensions();
      this.setupAnimation();
    }, 250));
    
    // Hover pause/resume
    if (this.config.pauseOnHover) {
      this.carousel.addEventListener('mouseenter', this.pauseAnimation.bind(this));
      this.carousel.addEventListener('mouseleave', () => {
        this.clearAllHoverEffects();
        this.resumeAnimation();
      });
    }
    
    // Enhanced hover interactions for expansion
    if (this.config.hoverExpand) {
      this.setupHoverExpansion();
    }
    
    // Drag functionality
    this.setupDragListeners();
    
    // Intersection Observer for performance
    this.setupIntersectionObserver();
  }
  
  setupDragListeners() {
    // Mouse (pointer) drag only. Touch devices never reach here (see init: they take
    // the native-scroll path), so there is no touch drag to fight the animation.
    this.track.addEventListener('mousedown', this.handleDragStart.bind(this));
    document.addEventListener('mousemove', this.handleDragMove.bind(this));
    document.addEventListener('mouseup', this.handleDragEnd.bind(this));
    
    // Prevent default drag behavior on images
    this.track.addEventListener('dragstart', e => e.preventDefault());
    
    // Disable links during a mouse drag
    this.track.addEventListener('click', this.handleLinkClick.bind(this), true);
  }
  
  handleDragStart(e) {
    this.isDragging = true;
    this.hasDragged = false;
    this.startX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
    
    // Clear any hover effects before starting drag
    this.clearAllHoverEffects();
    
    // Get current transform before stopping animation
    if (this.isAnimating) {
      this.currentTransform = this.getCurrentTransform();
    }
    
    this.track.style.animation = 'none';
    this.track.style.cursor = 'grabbing';
  }
  
  handleDragMove(e) {
    if (!this.isDragging) return;
    
    e.preventDefault();
    this.currentX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
    this.dragOffset = this.currentX - this.startX;
    
    if (Math.abs(this.dragOffset) > 5) {
      this.hasDragged = true;
    }
    
    const newTransform = this.currentTransform + this.dragOffset;
    this.track.style.transform = `translateX(${newTransform}px)`;
    
    // Check boundaries and wrap if needed
    this.checkAndWrapPosition(newTransform);
  }
  
  checkAndWrapPosition(transform) {
    const singleSetWidth = this.measureSetWidth();
    const maxLeft = -singleSetWidth * 2;
    const maxRight = singleSetWidth;
    
    if (transform > maxRight) {
      this.currentTransform -= singleSetWidth;
      this.track.style.transform = `translateX(${this.currentTransform + this.dragOffset}px)`;
    } else if (transform < maxLeft) {
      this.currentTransform += singleSetWidth;
      this.track.style.transform = `translateX(${this.currentTransform + this.dragOffset}px)`;
    }
  }

  handleDragEnd() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.track.style.cursor = 'grab';
    
    // Update current transform to include drag offset
    this.currentTransform += this.dragOffset;
    
    // Snap to nearest item if drag was significant
    if (Math.abs(this.dragOffset) > 50) {
      const direction = this.dragOffset > 0 ? 1 : -1;
      this.snapToItem(direction);
    } else {
      // Resume animation from current position
      this.resumeAnimationFromPosition();
    }
    
    this.dragOffset = 0;
    
    // Reset drag flag after a brief delay to prevent accidental clicks
    setTimeout(() => {
      this.hasDragged = false;
    }, 100);
  }
  
  handleLinkClick(e) {
    if (this.hasDragged) {
      e.preventDefault();
      e.stopPropagation();
    }
  }
  
  getCurrentTransform() {
    const style = window.getComputedStyle(this.track);
    const matrix = style.transform.match(/matrix.*\\((.+)\\)/);
    return matrix ? parseFloat(matrix[1].split(', ')[4]) : 0;
  }
  
  snapToItem(direction) {
    const itemWidth = this.itemWidth + 16;
    const snapOffset = direction * itemWidth;
    
    this.track.style.transition = 'transform 0.3s ease';
    this.currentTransform += snapOffset;
    this.track.style.transform = `translateX(${this.currentTransform}px)`;
    
    setTimeout(() => {
      this.track.style.transition = '';
      this.resumeAnimationFromPosition();
    }, 300);
  }
  
  resumeAnimationFromPosition() {
    const singleSetWidth = this.measureSetWidth();
    
    // Normalize position to safe range
    while (this.currentTransform > 0) {
      this.currentTransform -= singleSetWidth;
    }
    while (this.currentTransform < -singleSetWidth) {
      this.currentTransform += singleSetWidth;
    }
    
    // Create new animation starting from current position
    const endPosition = this.currentTransform - singleSetWidth;
    
    const keyframes = `
      @keyframes carousel-scroll-${this.carousel.dataset.sectionId} {
        from { transform: translateX(${this.currentTransform}px); }
        to { transform: translateX(${endPosition}px); }
      }
    `;
    
    // Update keyframes
    let styleSheet = document.getElementById(`carousel-keyframes-${this.carousel.dataset.sectionId}`);
    if (styleSheet) {
      styleSheet.textContent = keyframes;
    }
    
    // Restart animation
    this.track.style.animation = `carousel-scroll-${this.carousel.dataset.sectionId} ${this.config.animationLength * 10}s linear infinite`;
  }

  setupAccessibility() {
    // Add ARIA labels and roles
    this.carousel.setAttribute('role', 'region');
    this.track.setAttribute('aria-live', 'polite');
    
    // Make items focusable for keyboard navigation
    this.items.forEach((item, index) => {
      const link = item.querySelector('.carousel__link');
      if (link) {
        link.setAttribute('tabindex', index === 0 ? '0' : '-1');
      } else {
        item.setAttribute('tabindex', index === 0 ? '0' : '-1');
      }
    });
  }
  
  setupIntersectionObserver() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.startAnimation();
          } else {
            this.pauseAnimation();
          }
        });
      }, { threshold: 0.1 });
      
      observer.observe(this.carousel);
    }
  }
  
  setupHoverExpansion() {
    // Add hover event listeners to all carousel items
    const allItems = this.carousel.querySelectorAll('.carousel__item');
    
    allItems.forEach(item => {
      item.addEventListener('mouseenter', this.handleItemHover.bind(this));
      item.addEventListener('mouseleave', this.handleItemLeave.bind(this));
    });
  }
  
  handleItemHover(e) {
    const item = e.currentTarget;
    
    // Add a slight delay to prevent accidental triggers during fast scrolling
    item.hoverTimeout = setTimeout(() => {
      // Announce to screen readers that item is focused
      const link = item.querySelector('.carousel__link');
      const altText = item.querySelector('.carousel__image')?.alt || 'Carousel item';
      
      if (link) {
        link.setAttribute('aria-describedby', 'carousel-hover-description');
        
        // Create or update description element
        let description = document.getElementById('carousel-hover-description');
        if (!description) {
          description = document.createElement('div');
          description.id = 'carousel-hover-description';
          description.className = 'visually-hidden';
          description.setAttribute('aria-live', 'polite');
          document.body.appendChild(description);
        }
        description.textContent = `Hovering over ${altText}. Press Enter to navigate.`;
      }
    }, 100);
  }
  
  handleItemLeave(e) {
    const item = e.currentTarget;
    
    // Clear hover timeout
    if (item.hoverTimeout) {
      clearTimeout(item.hoverTimeout);
      item.hoverTimeout = null;
    }
    
    // Clean up accessibility attributes
    const link = item.querySelector('.carousel__link');
    if (link) {
      link.removeAttribute('aria-describedby');
    }
  }
  
  // Animation controls
  startAnimation() {
    if (!this.isAnimating && !this.isDragging) {
      this.track.style.animationPlayState = 'running';
      this.isAnimating = true;
    }
  }
  
  pauseAnimation() {
    if (this.isAnimating) {
      this.currentTransform = this.getCurrentTransform();
      this.track.style.animationPlayState = 'paused';
      this.isAnimating = false;
    }
  }
  
  clearAllHoverEffects() {
    const allItems = this.carousel.querySelectorAll('.carousel__item');
    allItems.forEach(item => {
      if (item.hoverTimeout) {
        clearTimeout(item.hoverTimeout);
        item.hoverTimeout = null;
      }
      
      const link = item.querySelector('.carousel__link');
      if (link) {
        link.removeAttribute('aria-describedby');
      }
    });
    
    // Clear description element
    const description = document.getElementById('carousel-hover-description');
    if (description) {
      description.textContent = '';
    }
  }
  
  resumeAnimation() {
    if (!this.isAnimating) {
      this.track.style.animationPlayState = 'running';
      this.isAnimating = true;
    }
  }
  
  toggleAnimation() {
    if (this.isAnimating) {
      this.pauseAnimation();
    } else {
      this.resumeAnimation();
    }
  }
  
  // Utility methods
  debounce(func, wait) {
    return (...args) => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
  
  // Cleanup method
  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    
    if (this.driftRAF) {
      cancelAnimationFrame(this.driftRAF);
    }
    
    if (this.resumeTimeout) {
      clearTimeout(this.resumeTimeout);
    }
    
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    // Clear any hover timeouts
    const allItems = this.carousel.querySelectorAll('.carousel__item');
    allItems.forEach(item => {
      if (item.hoverTimeout) {
        clearTimeout(item.hoverTimeout);
      }
    });
    
    // Remove accessibility description element
    const description = document.getElementById('carousel-hover-description');
    if (description) {
      description.remove();
    }
    
    // Remove event listeners
    window.removeEventListener('resize', this.handleResize);
    
    // Remove injected styles
    const styleSheet = document.getElementById(`carousel-keyframes-${this.carousel.dataset.sectionId}`);
    if (styleSheet) {
      styleSheet.remove();
    }
  }
}

// Initialize carousels when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('.carousel');
  carousels.forEach(carousel => {
    new ShopifyCarousel(carousel);
  });
});

// Handle Shopify theme editor events
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    const carousel = e.target.querySelector('.carousel');
    if (carousel) {
      new ShopifyCarousel(carousel);
    }
  });
  
  document.addEventListener('shopify:section:unload', (e) => {
    const carousel = e.target.querySelector('.carousel');
    if (carousel && carousel.carouselInstance) {
      carousel.carouselInstance.destroy();
    }
  });
}